import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { AutocompleteRenderInputParams, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";
import { grey } from "designSystem/styles/color";
import { Button, Chip, FlexRow, FlexRowCenter, Grid, IconButton, Message, OverflowText, SingleSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import OverflowTextField from "designSystem/components/textfield/OverflowTextField";
import { ComponentItem } from "designSystem/types/component-item-types";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, HTMLAttributes, ReactNode } from "react";
import { PROOF_PERMISSIONS, REVIEWER } from "../ProofService";
import { GreyLabelText } from "./ProofingStage";
import { Reviewer, Stage, useProofingStore } from "./ProofingStore";

const StyledSingleSelectForReviewers = styled(SingleSelect)(({ theme }) => ({
	marginRight: 4,
	width: 200,
}));

const ProofingOwnerChip: FunctionComponent = () => {
	return (
		<Chip
			res="Owner"
			sx={{
				borderRadius: 1,
				textTransform: "uppercase",
			}}
		/>
	);
};

interface ProofingSingleReviewerProps {
	/** the current review */
	reviewer: Reviewer;

	/** index */
	reviewerIndex: number;

	/** the stage which this review belongs to */
	stage: Stage;
}

const ProofingSingleReviewer: FunctionComponent<ProofingSingleReviewerProps> = observer((props) => {
	const { reviewer, reviewerIndex, stage } = props;
	const { isStageLockedAndStarted, isStageDecisionCalculationDecisionMadeBy, primaryDecisionMaker } = stage;
	const { proofOwner, stageEmailNotificationsOptions } = useProofingStore();
	const {
		disabled,
		role,
		userMatches,
		isUserDeletionDisabled,
		isUserSelectionDisabled,
		errorTextRes,
		hasError,
		isOwner,
	} = reviewer;

	// render owner chip in popper when reviewer is proof owner
	function renderOptionForReviewer<T extends ComponentItem>(props: HTMLAttributes<HTMLLIElement>, option: T): ReactNode {
		const {
			id,
			name,
		} = option;
		return (
			<li
				{...props}
				key={id}
			>
				<FlexRowCenter justifyContent="space-between">
					<OverflowText text={name} />
					{id === proofOwner &&
						<ProofingOwnerChip />}
				</FlexRowCenter>
			</li>
		);
	}

	const loc = useLocale();

	// render owner chip in text field when reviewer is proof owner
	function renderInputForReviewer(params: AutocompleteRenderInputParams): ReactNode {
		let endAdornment = params.InputProps.endAdornment;

		let helperText = "";
		if (!!primaryDecisionMaker && primaryDecisionMaker === reviewer.url && isStageDecisionCalculationDecisionMadeBy) {
			helperText = loc.resource("PrimaryDecisionMakerHelperText");
		}

		if (isOwner) {
			endAdornment = (
				<InputAdornment position="end">
					<ProofingOwnerChip />
				</InputAdornment>
			);
		}

		return (
			<OverflowTextField
				{...params}
				FormHelperTextProps={{ sx: { position: "relative", width: 600, top: 0 } }}
				InputProps={{
					...params.InputProps,
					endAdornment,
				}}
				error={hasError}
				helperText={helperText}
				helperTextRes={errorTextRes}
			/>
		);
	}

	return (
		<Grid item key={reviewerIndex}>
			{userMatches == 0 && (
				<Message
					mt={3}
					mx={3}
					titleRes="ProofingNoUserMatchesRole"
					titleResParams={[role]}
					variant="warning"
				>
					{{
						buttons: (
							<Button
								design="link"
								onClick={() => stage.closeReviewerWarning(reviewerIndex)}
								res="Dismiss"
							/>
						),
					}}
				</Message>
			)}
			{reviewer.userMatches > 5 && (
				<Message
					mt={3}
					mx={3}
					titleRes="MoreThanFiveUserMatchWarning"
					titleResParams={[userMatches, role]}
					variant="warning"
				>
					{{
						buttons: (
							<Grid
								container
								justifyContent="flex-end"
								spacing={2}
							>
								<Grid item>
									<Button
										design="secondary"
										onClick={() => stage.addAllMatchingUsers(reviewerIndex)}
										res="AddAll"
									/>
								</Grid>
								<Grid item>
									<Button
										design="link"
										onClick={() => stage.closeReviewerWarning(reviewerIndex)}
										res="Dismiss"
									/>
								</Grid>
							</Grid>
						),
					}}
				</Message>
			)}
			<FlexRowCenter p={3}>
				<FlexRow alignItems="flex-start">
					<StyledSingleSelectForReviewers
						disabled={isUserSelectionDisabled || isStageLockedAndStarted}
						name="username"
						onChange={(event, newValueItem, reason) => stage.onUserSelection(reviewerIndex, newValueItem)}
						options={stage.getDropDownUsers(reviewer)}
						renderInput={renderInputForReviewer}
						renderOption={renderOptionForReviewer}
						required
						value={stage.getReviewerValue(reviewerIndex)}
					/>
					<StyledSingleSelectForReviewers
						disabled={disabled || isStageLockedAndStarted}
						name="permission"
						onChange={(event, newValueItem, reason) => stage.onProofPermissionSelection(reviewerIndex, newValueItem)}
						options={PROOF_PERMISSIONS}
						required
						value={stage.getReviewerPermissionValue(reviewerIndex)}
					/>
					<StyledSingleSelectForReviewers
						onChange={(event, newValueItem, reason) => stage.onEmailNotificationSelection(reviewerIndex, newValueItem)}
						options={stageEmailNotificationsOptions}
						required
						value={stage.getReviewerEmailNotificationValue(reviewerIndex)}
					/>
					<FlexRow>
						<IconButton
							design="outline"
							disabled={isUserDeletionDisabled || isStageLockedAndStarted}
							onClick={() => stage.removeReviewer(reviewerIndex)}
							res={stage.hasOneReviewerRow ? "ProofsMustHaveUserOnStage" : null}
							showTooltipWhenDisabled
							size="large"
							sx={{
								alignSelf: role ? "flex-end" : "inherit",
							}}
						>
							<CloseIcon />
						</IconButton>
					</FlexRow>
				</FlexRow>
			</FlexRowCenter>
		</Grid>
	);
});

interface ProofingStageReviewersProps {
	stage: Stage;
}

export const ProofingStageReviewers: FunctionComponent<ProofingStageReviewersProps> = observer((props) => {
	const { stage } = props;
	const { isStageLockedAndStarted } = stage;
	return (
		<Grid
			sx={{
				backgroundColor: "white",
				borderTop: `1px solid ${grey[400]}`,
			}}
		>
			<FlexRowCenter
				flexGrow={1}
				pt={3}
				px={3.5}
			>
				<FlexRowCenter>
					<GreyLabelText
						bold
						res="AssigneesLabel"
					/>
				</FlexRowCenter>
				<FlexRowCenter
					paddingLeft={87}
				>
					<GreyLabelText
						bold
						res="EmailNotificationPreferenceLabel"
					/>
				</FlexRowCenter>
			</FlexRowCenter>
			{stage.reviewers.map((reviewer, reviewerIndex) => (
				<ProofingSingleReviewer
					key={reviewerIndex}
					reviewer={reviewer}
					reviewerIndex={reviewerIndex}
					stage={stage}
				/>
			))}
			<Grid item>
				<FlexRowCenter p={3}>
					<IconButton
						design="outline"
						disabled={isStageLockedAndStarted}
						onClick={() => stage.addReviewer(REVIEWER)}
						size="large"
					>
						<PersonAddIcon />
					</IconButton>
				</FlexRowCenter>
			</Grid>
		</Grid>
	);
});